import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import PageHeader from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Shield, AlertTriangle, CheckCircle, Zap, Lock, Code, Eye, Target, Users, Award, Clock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const AICodeSecurity = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      toast.success('Thanks! We\'ll get back to you within 24 hours.');
      setFormData({ name: '', email: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  const scrollToContact = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>AI Code Security & Vibe-Coded MVP Audits | Lovabuild</title>
        <meta
          name="description"
          content="Transform your AI-generated MVP into a production-ready, secure application. Expert security audits for vibe-coded products built with AI tools."
        />
        <meta
          name="keywords"
          content="AI code security, MVP security audit, AI-generated code, vibe coding security, AI code review, MVP security assessment"
        />
      </Helmet>

      <Navbar />

      <main>
        {/* Hero Section */}
        <PageHeader
          title={
            <>
              From <span className="bg-gradient-to-r from-gold via-primary to-gold bg-clip-text text-transparent">Vibe-Coded</span> to Production-Ready
            </>
          }
          subtitle="Your AI-built MVP shipped fast. But is it secure? We audit, fix, and fortify AI-generated code so you can scale with confidence."
          icon={<Shield className="w-5 h-5" />}
          badgeText="AI Code Security Experts"
        >
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Button size="lg" onClick={scrollToContact} className="group">
              Get Your Free Vibe Check
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button size="lg" variant="outline" onClick={scrollToContact}>
              Schedule Consultation
            </Button>
          </div>
        </PageHeader>

        {/* The Problem Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <AlertTriangle className="w-8 h-8 text-destructive" />
                <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                  The Hidden Cost of Speed: Are You Building a Security Time Bomb?
                </h2>
              </div>

              <p className="text-lg text-muted-foreground mb-8">
                AI coding tools are incredible for rapid prototyping. But speed comes with hidden risks that can cost you everything.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-destructive/20 bg-destructive/5">
                  <CardContent className="pt-6">
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2 text-foreground">
                      <Lock className="w-5 h-5 text-destructive" />
                      Common AI Code Vulnerabilities
                    </h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <span className="text-destructive mt-1">•</span>
                        <span><strong>Insecure defaults:</strong> AI often generates "quick" solutions without proper authentication</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-destructive mt-1">•</span>
                        <span><strong>Hardcoded secrets:</strong> API keys and credentials left exposed in code</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-destructive mt-1">•</span>
                        <span><strong>Broken access control:</strong> Users accessing data they shouldn't see</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-destructive mt-1">•</span>
                        <span><strong>SQL injection risks:</strong> Unvalidated user inputs creating database vulnerabilities</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-destructive mt-1">•</span>
                        <span><strong>Missing input validation:</strong> Zero sanitization on critical forms</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-gold/20 bg-gold/5">
                  <CardContent className="pt-6">
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2 text-foreground">
                      <Target className="w-5 h-5 text-gold" />
                      The Real-World Impact
                    </h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <span className="text-gold mt-1">•</span>
                        <span><strong>Data breaches:</strong> Customer data exposure kills trust instantly</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-gold mt-1">•</span>
                        <span><strong>Investor distrust:</strong> Due diligence reveals security gaps, deals fall apart</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-gold mt-1">•</span>
                        <span><strong>Costly rewrites:</strong> Fixing later costs 10x more than building right</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-gold mt-1">•</span>
                        <span><strong>Compliance nightmares:</strong> GDPR, CCPA, SOC 2 violations = legal trouble</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-gold mt-1">•</span>
                        <span><strong>Reputation damage:</strong> One breach can end your startup</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
                From <span className="text-gold">'Vibe-Coded'</span> to Production-Ready: Our AI-Specific Security Process
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Human expertise meets AI-powered tools. We don't just audit—we partner with you to build secure, scalable products.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
              <Card className="text-center">
                <CardContent className="pt-6">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Eye className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-foreground">1. Deep Audit</h3>
                  <p className="text-muted-foreground">
                    Comprehensive security scan of your entire codebase, database, and API endpoints
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="pt-6">
                  <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Target className="w-8 h-8 text-gold" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-foreground">2. Prioritize</h3>
                  <p className="text-muted-foreground">
                    Risk-ranked report: Critical fixes first, nice-to-haves later. Clear action plan.
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="pt-6">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Code className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-foreground">3. Remediate</h3>
                  <p className="text-muted-foreground">
                    We fix the vulnerabilities ourselves or guide your team through secure implementations
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="pt-6">
                  <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-gold" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-foreground">4. Guarantee</h3>
                  <p className="text-muted-foreground">
                    3-month security guarantee. If we miss something critical, we fix it free.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Offerings */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
                What We Secure & Optimize
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Specialized security audits for AI-generated applications across all platforms
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <Card>
                <CardContent className="pt-6">
                  <Zap className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-3 text-foreground">Custom SaaS & Web Apps</h3>
                  <p className="text-muted-foreground mb-4">
                    Full-stack security audits for React, Vue, Next.js applications with Supabase, Firebase, or custom backends.
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Authentication & authorization review</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Database security & RLS policies</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>API endpoint protection</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <Code className="w-10 h-10 text-gold mb-4" />
                  <h3 className="text-xl font-semibold mb-3 text-foreground">AI-Powered Workflows</h3>
                  <p className="text-muted-foreground mb-4">
                    Security audits for applications with AI integrations, chatbots, and automation tools.
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                      <span>API key & secret management</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                      <span>Rate limiting & abuse prevention</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                      <span>Prompt injection protection</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <Lock className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-3 text-foreground">WordPress & E-commerce</h3>
                  <p className="text-muted-foreground mb-4">
                    Plugin security audits, WooCommerce hardening, and payment integration reviews.
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Plugin vulnerability scanning</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Payment security compliance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>User data protection</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-foreground">
                Why Trust Lovabuild with Your AI-Built Product?
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Award className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-2 text-foreground">AI-Code Specialization</h3>
                        <p className="text-muted-foreground">
                          We built products with Lovable, Cursor, and v0. We know exactly where AI tools cut corners.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Shield className="w-5 h-5 text-gold" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-2 text-foreground">3-Month Security Guarantee</h3>
                        <p className="text-muted-foreground">
                          If we miss a critical vulnerability in our audit, we fix it free. No questions asked.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-2 text-foreground">Strategic Partnership</h3>
                        <p className="text-muted-foreground">
                          Not just a one-time fix. We become your ongoing security partner as you scale.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Code className="w-5 h-5 text-gold" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-2 text-foreground">Full-Stack Depth</h3>
                        <p className="text-muted-foreground">
                          Frontend, backend, database, infrastructure. We audit every layer of your stack.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-2 text-foreground">Fast Turnaround</h3>
                        <p className="text-muted-foreground">
                          Initial audit in 5-7 days. Critical fixes prioritized. You don't have to wait months.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Target className="w-5 h-5 text-gold" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-2 text-foreground">Founder-Focused Reports</h3>
                        <p className="text-muted-foreground">
                          Clear, jargon-free explanations. Understand risks without a security PhD.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Case Study */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-foreground">
                Don't Just Take Our Word For It
              </h2>

              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="pt-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gold rounded-full flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-foreground">
                        From Insecure MVP to Investor-Ready in 3 Weeks
                      </h3>
                      <p className="text-sm text-muted-foreground">SaaS Startup Founder</p>
                    </div>
                  </div>

                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    "We built our MVP in 2 weeks using Lovable and were getting traction fast. But when our first enterprise client asked for a security audit, we panicked. Lovabuild found 12 critical vulnerabilities including exposed API keys and broken authentication.
                  </p>

                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    They didn't just hand us a report—they fixed everything and taught our team how to maintain security standards. Three weeks later, we closed our first $50K enterprise deal with confidence. Best ROI ever."
                  </p>

                  <div className="flex items-center gap-4 pt-4 border-t border-primary/10">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map(i => (
                        <span key={i} className="text-gold text-xl">★</span>
                      ))}
                    </div>
                    <span className="text-sm text-muted-foreground">Completed in 21 days</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-foreground">
                Frequently Asked Questions
              </h2>

              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="item-1" className="bg-background border rounded-lg px-6">
                  <AccordionTrigger className="text-lg font-semibold text-foreground hover:text-primary">
                    How is auditing AI-generated code different from regular code reviews?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    AI tools like Lovable, Cursor, and v0 have specific patterns and common shortcuts they take. They often generate "happy path" code without proper error handling, input validation, or security considerations. We know these patterns intimately because we build with these tools ourselves. A traditional security audit might miss AI-specific vulnerabilities like insecure default configurations or missing authentication layers that AI commonly generates.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2" className="bg-background border rounded-lg px-6">
                  <AccordionTrigger className="text-lg font-semibold text-foreground hover:text-primary">
                    How long does a typical security audit take?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    Most audits take 5-7 business days for the initial assessment and report. Remediation time varies based on severity—critical issues can be fixed in 1-2 days, while comprehensive hardening might take 2-3 weeks. We prioritize based on risk so you can address the most dangerous vulnerabilities first while continuing development.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3" className="bg-background border rounded-lg px-6">
                  <AccordionTrigger className="text-lg font-semibold text-foreground hover:text-primary">
                    Do you fix the issues or just report them?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    Both! Our standard service includes a detailed audit report with prioritized findings. You can then choose: (1) Our team fixes the critical vulnerabilities directly in your codebase, or (2) We provide step-by-step remediation guides for your team with ongoing support. Most clients opt for a hybrid approach—we handle the critical security patches while training their team on security best practices for future development.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4" className="bg-background border rounded-lg px-6">
                  <AccordionTrigger className="text-lg font-semibold text-foreground hover:text-primary">
                    What if you miss something? What's your guarantee?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    We offer a 3-month security guarantee. If a critical vulnerability exists in code we audited and we didn't identify it in our report, we'll fix it at no additional charge. This covers any oversight on our part during the original audit scope. New features or code added after the audit would require a new assessment.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5" className="bg-background border rounded-lg px-6">
                  <AccordionTrigger className="text-lg font-semibold text-foreground hover:text-primary">
                    Is this only for apps built with specific AI tools?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    While we specialize in code generated by tools like Lovable, Cursor, v0, Bolt, and similar AI platforms, we audit any web application. Our expertise in AI-generated code means we're especially good at finding the subtle vulnerabilities these tools introduce, but our security principles apply to all modern web stacks (React, Vue, Next.js, Supabase, Firebase, Node.js, etc.).
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section id="contact-form" className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
                  Ready to Secure Your Innovation?
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Don't let security concerns slow down your momentum. Get a free consultation and discover what vulnerabilities might be lurking in your AI-built product.
                </p>
              </div>

              <Card className="max-w-2xl mx-auto">
                <CardContent className="pt-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-2 text-foreground">
                        Your Name *
                      </label>
                      <Input
                        id="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="John Doe"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2 text-foreground">
                        Email Address *
                      </label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="john@startup.com"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium mb-2 text-foreground">
                        Tell us about your project *
                      </label>
                      <Textarea
                        id="message"
                        required
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="What AI tools did you use? What stage is your MVP at? What are your main security concerns?"
                        rows={5}
                        className="w-full"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full group"
                    >
                      {isSubmitting ? 'Sending...' : 'Schedule Free Consultation'}
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>

                    <p className="text-xs text-center text-muted-foreground">
                      We typically respond within 24 hours. Your information is confidential.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default AICodeSecurity;